import { Box, useMediaQuery, useTheme } from '@ublend-npm/aulaui-core';
import React, { useEffect, useState } from 'react';
import AssignmentSidebar from './AssignmentSidebar/AssignmentSidebar';
import { createStyles } from './AssignmentResponsive.styles';
import NavigationBackHeader from '../../common/NavigationBackHeader/NavigationBackHeader';
import useFetchAssignments from './hooks/useFetchAssignments';
import { getSpaceBackRoute } from './utils/navigation';
import { getCurrentSpaceId } from '@core/selectors/space';
import { useSelector } from 'react-redux';
import { AccessTokenState, AulaState, Space, User } from '@core/types/state';
import {
  getAssignments,
  selectCurrentAssignmentId,
} from '@core/assignments/selectors';
import pushRoute from '@app/utils/routing';
import AssignmentDetails from './AssignmentDetails/AssignmentDetails';
import { ResponsiveAssignment } from '@core/assignments/types';
import MaterialPreviewModal from './MaterialPreviewModal/MaterialPreviewModal';

type Props = {
  accessTokens: AccessTokenState;
  params?: {
    assignmentId?: string;
  };
  fetchAccessToken: ({
    spaceId,
  }: {
    spaceId: string;
  }) => Promise<string | null>;
  fetchAssignments: (params: {
    user: User | null;
    accessToken: string;
    spaceId: string;
  }) => void;
  fetchSubmissions: (params: {
    accessToken: string;
    assignmentId: string;
  }) => void;
  fetchAllSubmissionsIncludingExtensions: (params: {
    accessToken: string;
    assignmentsIds: string[];
  }) => void;
  isFetching: boolean;
  isEducator?: boolean;
  baseDomain: string;
  needsToRefreshAccessTokenProp: () => boolean;
  selectedSpace?: Space;
  user: User | null;
  isStaff: boolean;
  showEmptyCohortAlert: boolean;
  _submissionFetchParams: {
    accessToken?: string;
    resolvedAssignmentId: string;
    hasExtensions: boolean;
    extensionIds: string[];
    hasFetchedSubmissions: boolean;
    isFetchingSubmissions: boolean;
  };
};

const AssignmentsResponsive = ({
  accessTokens,
  params,
  fetchAccessToken,
  fetchAssignments,
  fetchSubmissions,
  fetchAllSubmissionsIncludingExtensions,
  isFetching,
  isEducator = false,
  baseDomain,
  needsToRefreshAccessTokenProp,
  selectedSpace,
  user,
  isStaff = false,
  showEmptyCohortAlert,
  _submissionFetchParams,
}: Props) => {
  const theme = useTheme();
  const styles = createStyles(theme);
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'));
  const state = useSelector((state: AulaState) => state);
  const spaceId = getCurrentSpaceId(state);
  const assignments = getAssignments(state);
  const currentAssignmentId = useSelector(selectCurrentAssignmentId);
  const routeAssignmentId = params?.assignmentId;
  const backRoute = getSpaceBackRoute(spaceId);
  const baseRoute = `/dashboard/${spaceId}/journey/assignments`;
  const restoreKey = selectedSpace?.objectId
    ? `assignments:lastVisited:${selectedSpace.objectId}`
    : null;
  const [highlightedAssignmentId, setHighlightedAssignmentId] = useState<
    string | null
  >(null);
  const [previewState, setPreviewState] = useState<{
    open: boolean;
    materialId: string | null;
    materialTitle: string;
  }>({
    open: false,
    materialId: null,
    materialTitle: '',
  });
  useFetchAssignments({
    accessTokens,
    fetchAccessToken,
    fetchAssignments,
    isFetching,
    needsToRefreshAccessTokenProp,
    selectedSpace,
    user,
  });

  useEffect(() => {
    const {
      accessToken,
      resolvedAssignmentId,
      hasExtensions,
      extensionIds,
      hasFetchedSubmissions,
      isFetchingSubmissions,
    } = _submissionFetchParams;
    if (
      !accessToken ||
      !resolvedAssignmentId ||
      hasFetchedSubmissions ||
      isFetchingSubmissions
    )
      return;
    if (hasExtensions) {
      fetchAllSubmissionsIncludingExtensions({
        accessToken,
        assignmentsIds: [resolvedAssignmentId, ...extensionIds],
      });
    } else {
      fetchSubmissions({ accessToken, assignmentId: resolvedAssignmentId });
    }
  }, [
    _submissionFetchParams.resolvedAssignmentId,
    _submissionFetchParams.hasFetchedSubmissions,
    _submissionFetchParams.isFetchingSubmissions,
  ]);

  const handleFetchAccessToken = async (spaceId: string) => {
    if (needsToRefreshAccessTokenProp()) {
      return fetchAccessToken({ spaceId });
    }

    return accessTokens[spaceId]?.token || fetchAccessToken({ spaceId });
  };

  const handleFetchAssignments = async () => {
    const selectedSpaceId = selectedSpace?.objectId;

    if (isFetching || !selectedSpaceId) {
      return;
    }

    const accessToken = await handleFetchAccessToken(selectedSpaceId);

    if (accessToken) {
      fetchAssignments({
        user,
        accessToken,
        spaceId: selectedSpaceId,
      });
    }
  };

  useEffect(() => {
    handleFetchAssignments();
  }, [selectedSpace?.objectId]);

  useEffect(() => {
    if (!selectedSpace?.objectId || !restoreKey) {
      setHighlightedAssignmentId(null);
      return;
    }

    const lastVisitedAssignmentId = sessionStorage.getItem(restoreKey);

    if (!lastVisitedAssignmentId) {
      setHighlightedAssignmentId(null);
      return;
    }

    setHighlightedAssignmentId(lastVisitedAssignmentId);
    sessionStorage.removeItem(restoreKey);
  }, [restoreKey, selectedSpace?.objectId]);

  const openMaterialPreview = (
    materialId: string,
    materialTitle?: string,
    assignmentId?: string
  ) => {
    if (!selectedSpace?.objectId) return;
    if (assignmentId) {
      sessionStorage.setItem(
        `assignments:lastVisited:${selectedSpace.objectId}`,
        assignmentId
      );
      setHighlightedAssignmentId(assignmentId);
    }
    setPreviewState({
      open: true,
      materialId,
      materialTitle: materialTitle || '',
    });
  };

  const closeMaterialPreview = () => {
    setPreviewState({
      open: false,
      materialId: null,
      materialTitle: '',
    });
  };

  const clearRestoredAssignment = () => {
    if (!selectedSpace?.objectId) {
      return;
    }

    sessionStorage.removeItem(
      `assignments:lastVisited:${selectedSpace.objectId}`
    );
    setHighlightedAssignmentId(null);
  };

  const handleBackFromAssignments = () => {
    clearRestoredAssignment();
    if (backRoute) {
      pushRoute(backRoute);
    }
  };

  const handleBackToAssignmentList = () => {
    clearRestoredAssignment();
    pushRoute(`${baseRoute}/list`);
  };

  const [defaultAssignmentId, setDefaultAssignmentId] = useState<string>('');

  const firstAssignment = assignments[0];
  useEffect(() => {
    if (firstAssignment) {
      setDefaultAssignmentId(
        firstAssignment?.id ||
          firstAssignment?.objectId ||
          firstAssignment?.externalId
      );
    }
  }, [firstAssignment]);

  useEffect(() => {
    // Use the ID from the matched route rather than the Redux routing state.
    // Redux updates before this component unmounts when navigating away, which
    // can briefly clear currentAssignmentId and otherwise push users back here.
    if (routeAssignmentId) {
      return;
    }

    if (!defaultAssignmentId) {
      return;
    }

    pushRoute(`${baseRoute}/${defaultAssignmentId}?from=myspacenav`);
  }, [baseRoute, routeAssignmentId, defaultAssignmentId]);

  useEffect(() => {
    if (isMdUp && currentAssignmentId == 'list' && defaultAssignmentId) {
      pushRoute(`${baseRoute}/${defaultAssignmentId}`);
    }
  }, [baseRoute, currentAssignmentId, defaultAssignmentId, isMdUp]);

  // Determine the current user role based on the educator and staff flags
  const currentUserRole = isEducator
    ? 'educator'
    : isStaff
    ? 'staff'
    : 'student';

  const getContent = () => {
    if (isMdUp) {
      return (
        <Box sx={styles.containerWrapper}>
          <Box sx={styles.leftWrapper}>
            <AssignmentSidebar
              assignments={assignments as ResponsiveAssignment[]}
              backRoute={backRoute}
              onBackClick={handleBackFromAssignments}
              spaceId={spaceId}
              baseDomain={baseDomain}
              isEducator={isEducator}
              highlightedAssignmentId={highlightedAssignmentId}
              onMaterialClick={openMaterialPreview}
              isDesktop={isMdUp}
              fetchAccessToken={handleFetchAccessToken}
              fetchAssignments={handleFetchAssignments}
            />
          </Box>
          <Box sx={styles.assignmentDetailsWrapper}>
            <AssignmentDetails
              currentUserRole={currentUserRole}
              assignments={assignments as ResponsiveAssignment[]}
              showEmptyCohortAlert={showEmptyCohortAlert}
            />
          </Box>
        </Box>
      );
    } else {
      return (
        <Box sx={styles.pageWrapper}>
          <Box sx={styles.buttonWrapper}>
            <NavigationBackHeader onClick={handleBackToAssignmentList} />
          </Box>
          <Box sx={styles.pageContent}>
            <Box sx={styles.assignmentDetailsMobile}>
              <AssignmentDetails
                currentUserRole={currentUserRole}
                assignments={assignments as ResponsiveAssignment[]}
                showEmptyCohortAlert={showEmptyCohortAlert}
              />
            </Box>
          </Box>
        </Box>
      );
    }
  };

  return (
    <>
      {/* On mobile, show the full-screen sidebar list when the route is /list.
          On desktop, getContent() always renders the split-pane shell which
          already includes the sidebar — so never show the standalone list on md+. */}
      {!isMdUp && currentAssignmentId == 'list' ? (
        <Box sx={{ height: '100%' }}>
          <AssignmentSidebar
            assignments={assignments as ResponsiveAssignment[]}
            backRoute={backRoute}
            spaceId={spaceId}
            spaceName={selectedSpace?.name}
            baseDomain={baseDomain}
            isEducator={isEducator}
            highlightedAssignmentId={highlightedAssignmentId}
            onMaterialClick={openMaterialPreview}
            isDesktop={isMdUp}
            fetchAccessToken={handleFetchAccessToken}
            fetchAssignments={handleFetchAssignments}
          />
        </Box>
      ) : (
        <Box sx={styles.assignments}>{getContent()}</Box>
      )}
      <MaterialPreviewModal
        open={previewState.open}
        onClose={closeMaterialPreview}
        onGoToPage={() => {
          if (previewState.materialId) {
            pushRoute(
              `/dashboard/${spaceId}/journey/materials/${previewState.materialId}?from=assignments`
            );
          }
        }}
        spaceId={selectedSpace?.objectId}
        materialId={previewState.materialId}
        previewTitle={previewState.materialTitle}
      />
    </>
  );
};

export default AssignmentsResponsive;
